import type { JoinFormValues, LicenseEntry } from './components/form-values'
import type { JoinStepId } from './join-step-types'

export type JoinFormDraft = {
  values: JoinFormValues
  currentStepIndex: number
  tosAccepted: boolean
  privacyAccepted: boolean
  licenseEntries: Record<string, LicenseEntry>
  skippedSteps: JoinStepId[]
}

export type WaiverProgress = {
  collectedIds: string[]
  activeSignatureRequestId?: string
}

const FORM_PREFIX = 'bb.join.form.'
const WAIVER_PREFIX = 'bb.join.waiver.'

function readSession(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.sessionStorage
  } catch {
    return null
  }
}

function readDraft<T>(key: string): T | null {
  const store = readSession()
  if (!store) return null
  try {
    const raw = store.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function writeDraft<T>(key: string, value: T): void {
  const store = readSession()
  if (!store) return
  try {
    store.setItem(key, JSON.stringify(value))
  } catch {
    return
  }
}

function removeDraft(key: string): void {
  const store = readSession()
  if (!store) return
  try {
    store.removeItem(key)
  } catch {
    return
  }
}

export function loadJoinFormDraft(token: string): JoinFormDraft | null {
  if (!token) return null
  return readDraft<JoinFormDraft>(`${FORM_PREFIX}${token}`)
}

export function saveJoinFormDraft(token: string, draft: JoinFormDraft): void {
  if (!token) return
  writeDraft(`${FORM_PREFIX}${token}`, draft)
}

export function clearJoinFormDraft(token: string): void {
  if (!token) return
  removeDraft(`${FORM_PREFIX}${token}`)
}

export function waiverProgressKey(token: string): string {
  return `${WAIVER_PREFIX}${token}`
}

export function loadWaiverProgress(key: string): WaiverProgress | null {
  return readDraft<WaiverProgress>(key)
}

export function saveWaiverProgress(key: string, progress: WaiverProgress): void {
  writeDraft(key, progress)
}

export function clearWaiverProgress(key: string): void {
  removeDraft(key)
}
